import '@fontsource/outfit/400.css';
import '@fontsource/outfit/500.css';
import '@fontsource/outfit/600.css';
import '@fontsource/outfit/700.css';
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';
import "@/App.css";
import './styles/xtream.css';
import './styles/livetv.css';
import './styles/vod.css';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import XtreamDashboard from './pages/XtreamDashboard';
import LiveTVPage from './pages/LiveTVPage';
import VODPage from './pages/VODPage';
import SeriesPage from './pages/SeriesPage';
import SeriesDetailPage from './pages/SeriesDetailPage';
import WatchPage from './pages/WatchPage';
import FavoritesPage from './pages/FavoritesPage';
import HistoryPage from './pages/HistoryPage';
import CategoriesPage from './pages/CategoriesPage';
import EPGPage from './pages/EPGPage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';

// Components
import Layout from './components/Layout';

// Protected Route Component (with Layout)
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[rgb(5,5,5)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[rgb(124,58,237)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Protected Route Component (without Layout - for full-screen pages)
function ProtectedRouteNoLayout({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[rgb(5,5,5)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[rgb(124,58,237)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Admin Route Component
function AdminRoute({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[rgb(5,5,5)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[rgb(124,58,237)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Public Route (redirect to home if logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[rgb(5,5,5)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[rgb(124,58,237)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRouteNoLayout><XtreamDashboard /></ProtectedRouteNoLayout>} />
          <Route path="/live" element={<ProtectedRouteNoLayout><LiveTVPage /></ProtectedRouteNoLayout>} />
          <Route path="/movies" element={<ProtectedRouteNoLayout><VODPage /></ProtectedRouteNoLayout>} />
          <Route path="/series" element={<ProtectedRouteNoLayout><SeriesPage /></ProtectedRouteNoLayout>} />
          <Route path="/series/:seriesId" element={<ProtectedRouteNoLayout><SeriesDetailPage /></ProtectedRouteNoLayout>} />
          <Route path="/channels" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/watch/:channelId" element={<ProtectedRoute><WatchPage /></ProtectedRoute>} />
          <Route path="/watch/ep_:episodeId" element={<ProtectedRouteNoLayout><WatchPage /></ProtectedRouteNoLayout>} />
          <Route path="/watch-vod/:vodId" element={<ProtectedRouteNoLayout><WatchPage /></ProtectedRouteNoLayout>} />
          <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
          <Route path="/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
          <Route path="/epg" element={<ProtectedRoute><EPGPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      
      {/* Toast Notifications */}
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: 'rgb(15, 15, 16)',
            color: 'rgb(250, 250, 250)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;
